import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import AuthService from '../../services/AuthService';

export interface UserPaymentMethodData {
  cardLast4?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
}

type PaymentMethodFormProps = {
    userId: string;
    paymentFailed: boolean;
    onSuccess?: (paymentMethod: UserPaymentMethodData) => void;
    // El cobro no se ha completado (rechazado o 3DS pendiente) pero la tarjeta nueva ya es la que figura en el cliente
    // El motivo del nuevo rechazo, si la API lo devuelve, puede ser distinto al que ya figuraba
    onCardUpdated?: (paymentMethod: UserPaymentMethodData, paymentFailedReason?: string) => void;
};

const PaymentMethodForm = (props: PaymentMethodFormProps) => {
    const stripe = useStripe();
    const elements = useElements();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const handleSubmit = async () => {
        if (!stripe || !elements || !props.userId) {
            // Stripe aún no está cargado
            setError("Ha ocurrido un error. Inténtalo más tarde.");
            return;
        }

        // Validación de los elementos de Stripe
        const cardNumberElement = elements.getElement(CardNumberElement);
        const cardExpiryElement = elements.getElement(CardExpiryElement);
        const cardCvcElement = elements.getElement(CardCvcElement);
        
        if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
          setError("Ha ocurrido un error. Inténtalo más tarde.");
          return;
        }

        setLoading(true);
        setError(undefined);
    
        const { token, error: stripeError } = await stripe.createToken(cardNumberElement);
    
        if (stripeError) {
          setLoading(false);
          setError(stripeError.message);
          return;
        }

        try {
            const response = await AuthService.updateSecurePaymentMethod(props.userId, { token });
            const { clientSecret, confirmed, paymentMethod } = response.data;
            
            if (confirmed) {
              setLoading(false);
              props.onSuccess?.(paymentMethod);
              return;
            }

            if (!clientSecret && !confirmed) {
                setLoading(false);
                props.onSuccess?.(paymentMethod);
                return;
            }
            
            // La tarjeta nueva ya es la del cliente aunque la autenticación falle
            props.onCardUpdated?.(paymentMethod);

            // El PaymentIntent de la factura ya lleva la tarjeta nueva: solo se completa la autenticación (si se pasara el
            // payment_method se crearía una segunda tarjeta idéntica en el cliente)
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
        
            if (error) {
              setError(error.message);
              setLoading(false);
              return;
            } else if (paymentIntent.status === 'succeeded') {
              setLoading(false);
              props.onSuccess?.(paymentMethod);
              return;
            } else if (paymentIntent.status === 'requires_action') {
              setError('Se requiere autenticación adicional para completar el pago.');
              setLoading(false);
            }  else if (paymentIntent.status === 'requires_payment_method') {
              // Si el PaymentIntent requiere un método de pago válido
              setError('El método de pago proporcionado no es válido. Por favor, intente con otro.');
              setLoading(false);
            } else if (paymentIntent.status === 'canceled') {
              // Si el PaymentIntent fue cancelado
              setError('El pago fue cancelado. Por favor, intente nuevamente.');
              setLoading(false);
            } else {
              setError('Hubo un problema con el pago, por favor intente nuevamente.');
              setLoading(false);
            }

          } catch (error: any) {
            if (error.response?.status === 402) {
              // Cobro rechazado por el banco: no es una errata, se vacían los datos de la tarjeta
              cardNumberElement.clear();
              cardExpiryElement.clear();
              cardCvcElement.clear();
              if (error.response.data?.paymentMethod) props.onCardUpdated?.(error.response.data.paymentMethod, error.response.data.paymentFailedReason);
            }
            setError(error.response?.data?.message || "Error al procesar el pago. Por favor, intente nuevamente.");
            setLoading(false);
          }
    };

    return (
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                mt: 1, }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 16 }}>
                  Introduce tus nuevos datos de pago
                </Typography>

                <Box sx={{
                  width: '100%',
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.1)',
                  backgroundColor: 'background.default',
                }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ border: "1px solid #ccc", borderRadius: 2, padding: "0.8rem" }}>
                      <CardNumberElement options={{ style: { base: { fontSize: '16px' } } }} />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ border: "1px solid #ccc", borderRadius: 2, padding: "0.8rem" }}>
                      <CardExpiryElement options={{ style: { base: { fontSize: '16px' } } }} />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ border: "1px solid #ccc", borderRadius: 2, padding: "0.8rem" }}>
                      <CardCvcElement options={{ style: { base: { fontSize: '16px' } } }} />
                    </Box>
                  </Grid>
                </Grid>
                </Box>
              </Box>

              <Button
                variant="contained"
                color="success"
                fullWidth
                onClick={handleSubmit}
                disabled={loading || !stripe || !elements}
                sx={{
                  mt: 1,
                  mb: 1,
                  py: 1.2,
                  fontWeight: 'bold',
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Actualizar método de pago'}
              </Button>

              <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: 12, mb: 1 }}>
                {props.paymentFailed ? (
                  <>
                    Al actualizar tu tarjeta, Zyra intentará cobrar el importe pendiente. <br />
                    Si el pago se completa, tu suscripción se reactivará automáticamente. <br />
                    Puedes cancelar tu suscripción en cualquier momento.
                  </>
                ) : (
                  <>
                    Los futuros pagos de tu suscripción se cargarán a la nueva tarjeta. <br />
                    Puedes actualizarla en cualquier momento desde tu cuenta.
                  </>
                )}
              </Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 10, textAlign: 'center' }}>
                <a href="https://stripe.com/es/legal/consumer" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                  Condiciones
                </a>
                <span style={{ margin: '0 8px' }}>|</span>
                <a href="https://stripe.com/es/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                  Privacidad
                </a>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
    );
};

export default PaymentMethodForm;
